import {
    Button,
    Card,
    Form,
    FormControl,
    Heading,
    If,
    Input,
    Link,
    Notification,
    NotificationContent,
    Stack,
    Text,
    View,
    useAlert,
} from '@fold-dev/core'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router'
import { useSearchParams } from 'react-router-dom'
import { Loading } from '../components'
import { logger, validEmail } from '../helpers/util'
import { useAuth } from '../hooks/auth.hook'
import { LogoNew } from '../logo'

export const Disclaimer = () => {
    return (
        <Text
            textAlign="center"
            colorToken="text-weaker">
            By using Fronteer, you agree to our{' '}
            <a
                href="https://fronteer.app/terms-of-use"
                target="_blank">
                terms of use
            </a>{' '}
            and{' '}
            <a
                href="https://fronteer.app/license"
                target="_blank">
                license agreement
            </a>
            .
        </Text>
    )
}

export const AuthLayout = (props: any) => {
    const { currentAuthenticatedUser, parseJwt, login, signup, resetPassword, updatePassword } = useAuth()
    const [view, setView] = useState<'password' | 'token' | 'signup' | 'signin'>('signin')
    const [code, setCode] = useState('')
    const [email, setEmail] = useState('')
    const [password, setPassword] = useState('')
    const [passwordConfirm, setPasswordConfirm] = useState('')
    const [name, setName] = useState('')
    const [error, setError] = useState<any>(null)
    const [loading, setLoading] = useState(false)
    const navigate = useNavigate()
    const { alert } = useAlert()
    const [searchParams] = useSearchParams()
    const token = searchParams.get('token')

    const handleSignup = async (e) => {
        e.preventDefault()

        if (passwordConfirm != password) return setError('Passwords do not match.')
        if (!validEmail(email)) return setError('Please use a valid email address.')
        if (code != 'ad89-a26b55224dfd') return setError('Incorrect beta code.')

        setLoading(true)
        setError(null)

        try {
            await signup({ email, name, password, code })

            setLoading(false)
            alert({
                title: 'Verify Email',
                description:
                    'Please verify your email address first. Check your mailbox for instructions on how to verify your email address.',
                button: 'Okay',
                closeButton: true,
                onDismiss: () => setView('signin'),
            })
        } catch (e) {
            setLoading(false)
            setError(typeof e == 'string' ? e : 'An error occured')
        }
    }

    const handleSignin = async (e) => {
        e.preventDefault()

        setLoading(true)
        setError(null)

        try {
            await login({ email, password })

            setLoading(false)
            navigate('/app')
        } catch (e) {
            setLoading(false)
            setError(typeof e == 'string' ? e : 'An error occured')
        }
    }

    const handleResetPassword = async (e) => {
        e.preventDefault()

        setLoading(true)
        setError(null)

        try {
            await resetPassword({ email })

            setLoading(false)
            alert({
                title: 'Success',
                description: 'Check your mailbox for instructions on how to reset your password.',
                button: 'Okay',
                closeButton: true,
                onDismiss: () => setView('signin'),
            })
        } catch (e) {
            setLoading(false)
            setError(typeof e == 'string' ? e : 'An error occured')
        }
    }

    const handleUpdatePassword = async (e) => {
        e.preventDefault()
        if (passwordConfirm != password) return setError('Passwords do not match.')
        if (!token) return setError('Invalid token')

        setLoading(true)
        setError(null)

        try {
            await updatePassword({ token, password })

            setLoading(false)
            alert({
                title: 'Success',
                description: 'You have successfully updated your password. You can now sign in with the new password.',
                button: 'Okay',
                closeButton: true,
                onDismiss: () => setView('signin'),
            })
        } catch (e) {
            setLoading(false)
            setError(typeof e == 'string' ? e : 'An error occured')
        }
    }

    useEffect(() => {
        if (token) return setView('token')

        currentAuthenticatedUser()
            .then((res) => {
                const { token } = res
                const { sub } = parseJwt(token)
                navigate('/app')
            })
            .catch((error) => {
                logger(error)
            })
    }, [token])

    useEffect(() => {
        setError(null)
        setEmail('')
        setName('')
        setPasswordConfirm('')
        setPassword('')
    }, [view])

    return (
        <>
            <If if={!!error}>
                <Notification
                    width="100%"
                    variant="danger">
                    <NotificationContent>
                        <Text>{error}</Text>
                    </NotificationContent>
                </Notification>
            </If>

            <View
                column
                width="100%"
                height="100%"
                position="absolute"
                bg="linear-gradient(45deg, var(--f-color-surface), var(--f-color-surface-strong))"
                style={{ '--f-color-accent': 'var(--f-color-accent-500)' } as any}>
                <Card
                    column
                    p="3rem 2rem"
                    width={425}
                    className="auth-layout">
                    <Loading loading={loading} />

                    <View p="0 0 2rem 0">
                        <LogoNew
                            scale={0.5}
                            color="var(--f-color-text)"
                        />
                    </View>

                    <If if={view == 'password'}>
                        <Form
                            onSubmit={handleResetPassword}
                            width="100%">
                            <Stack
                                direction="vertical"
                                spacing={20}
                                justifyContent="center"
                                noStretch>
                                <Heading>Reset your password</Heading>

                                <FormControl>
                                    <Input
                                        type="email"
                                        name="email"
                                        value={email}
                                        placeholder="Email"
                                        onChange={(e) => setEmail(e.target.value)}
                                    />
                                </FormControl>

                                <Button
                                    size="xl"
                                    type="submit"
                                    variant="accent"
                                    disabled={loading || !email}>
                                    Send Reset Code
                                </Button>

                                <Link onClick={() => setView('signin')}>Go back to sign in</Link>
                            </Stack>
                        </Form>
                    </If>

                    <If if={view == 'token'}>
                        <Form
                            onSubmit={handleUpdatePassword}
                            width="100%">
                            <Stack
                                direction="vertical"
                                spacing={20}
                                justifyContent="center"
                                noStretch>
                                <Heading>Update your password</Heading>
                                <Text textAlign="center">Create your new password, make sure it's good!</Text>

                                <FormControl>
                                    <Input
                                        type="password"
                                        name="password"
                                        value={password}
                                        placeholder="New password"
                                        onChange={(e) => setPassword(e.target.value)}
                                    />
                                </FormControl>

                                <FormControl>
                                    <Input
                                        type="password"
                                        name="confirm"
                                        value={passwordConfirm}
                                        placeholder="Verify new password"
                                        onChange={(e) => setPasswordConfirm(e.target.value)}
                                    />
                                </FormControl>

                                <Button
                                    size="xl"
                                    type="submit"
                                    variant="accent"
                                    disabled={loading || !password || !passwordConfirm}>
                                    Update Password
                                </Button>

                                <Link onClick={() => setView('password')}>Send another code</Link>

                                <Link onClick={() => setView('signin')}>Go back to sign in</Link>
                            </Stack>
                        </Form>
                    </If>

                    <If if={view == 'signup'}>
                        <Form
                            onSubmit={handleSignup}
                            width="100%">
                            <Stack
                                direction="vertical"
                                spacing={20}
                                justifyContent="center"
                                noStretch>
                                <Heading>Create an account</Heading>

                                <FormControl>
                                    <Input
                                        type="text"
                                        name="name"
                                        value={name}
                                        placeholder="Full name"
                                        onChange={(e) => setName(e.target.value)}
                                    />
                                </FormControl>

                                <FormControl>
                                    <Input
                                        type="email"
                                        name="email"
                                        value={email}
                                        placeholder="Email"
                                        onChange={(e) => setEmail(e.target.value)}
                                    />
                                </FormControl>

                                <FormControl>
                                    <Input
                                        type="password"
                                        name="password"
                                        autoComplete="off"
                                        value={password}
                                        placeholder="Password"
                                        onChange={(e) => setPassword(e.target.value)}
                                    />
                                </FormControl>

                                <FormControl>
                                    <Input
                                        type="password"
                                        name="confirm"
                                        autoComplete="off"
                                        value={passwordConfirm}
                                        placeholder="Confirm password"
                                        onChange={(e) => setPasswordConfirm(e.target.value)}
                                    />
                                </FormControl>

                                      <FormControl>
                                    <Input
                                        type="text"
                                        name="code"
                                        autoComplete="off"
                                        value={code}
                                        placeholder="Beta Access Code"
                                        onChange={(e) => setCode(e.target.value)}
                                    />
                                </FormControl>

                                <Button
                                    size="xl"
                                    type="submit"
                                    variant="accent"
                                    disabled={loading || !email || !password || !passwordConfirm}>
                                    Create Account
                                </Button>

                                <Link onClick={() => setView('signin')}>Go back to sign in</Link>

                                <Disclaimer />
                            </Stack>
                        </Form>
                    </If>

                    <If if={view == 'signin'}>
                        <Form
                            onSubmit={handleSignin}
                            width="100%">
                            <Stack
                                direction="vertical"
                                spacing={20}
                                justifyContent="center"
                                noStretch>
                                <Heading>Sign in</Heading>
                                <Text textAlign="center">Please log in using your email & password</Text>

                                <FormControl>
                                    <Input
                                        autoComplete="off"
                                        name="email"
                                        type="email"
                                        placeholder="Email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                    />
                                </FormControl>

                                <FormControl>
                                    <Input
                                        type="password"
                                        name="password"
                                        value={password}
                                        placeholder="Password"
                                        onChange={(e) => setPassword(e.target.value)}
                                    />
                                </FormControl>

                                <Button
                                    size="xl"
                                    type="submit"
                                    variant="accent"
                                    disabled={loading || !password || !email}>
                                    Sign in
                                </Button>

                                <Link onClick={() => setView('password')}>I've lost my password</Link>

                                <Link onClick={() => setView('signup')}>Create an account</Link>

                                <Disclaimer />
                            </Stack>
                        </Form>
                    </If>
                </Card>
            </View>
        </>
    )
}
